import React, { useEffect } from 'react'
import { Checkbox, Col, Form, Row, Typography } from 'antd'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import moment from 'moment'
import ButtonGlobal from '@shared/Button'
import style from './regulationLimits.module.scss'
import { t } from 'i18next'
import { InputField } from '@shared/InputField'
import DatePicker from '@shared/DatePicker/DatePicker'
import {
  LimitCurrentStatus,
  statusStyles,
} from 'pages/BusinessCards/components/LimitsPage/constants/constantForLimits'
import { ModalProvider, useCustomModal } from '@shared/Modal'
import AssignLimit from './AssignLimit'
import { FormValues } from './types'
import DisabledLimit from './DisabledLimit'
import { useFetchByDocTypeMutation } from 'store/reducers/limitsforBusinnesCard/cardLimits.api'
import { QueryStatus } from '@reduxjs/toolkit/query'
import { InformationBlueIcon } from '@modules/CustomIcons'
import CustomTooltip from '../../../../../../../../components/Tooltip'

function RegulationLimits({
  hasPrevStartValue,
}: {
  hasPrevStartValue: boolean
}) {
  const {
    formState: { errors, isValid },
    control,
    watch,
    setValue,
    trigger,
    getValues,
    resetField,
  } = useFormContext<FormValues>()
  const { closeModal, status: modalStatus, open, setStatus } = useCustomModal()

  const [fetchDoctype] = useFetchByDocTypeMutation()
  useEffect(() => {
    if (open) {
      fetchDoctype({
        isClickCardBlockButton: false,
        docType: 'LMT',
      })
        .unwrap()
        .then(() => setStatus({ status: '' }))
        .catch((err) =>
          setStatus({ status: QueryStatus.rejected, message: err?.data })
        )
    }
  }, [open])

  const checked = watch('toExpDate')
  const status = watch('statusForLabel')
  const watchFields = useWatch({ name: ['toDate', 'fromDate'], control })
  const setEndDate = (checked: boolean) => {
    if (checked) {
      setValue(
        'toDate',
        moment(getValues('expDate'), 'DD.MM.YYYY').format('DD.MM.YYYY'),
        { shouldValidate: true }
      )
    } else {
      resetField('toDate')
    }
  }
  useEffect(() => {
    if (!hasPrevStartValue) {
      setValue('fromDate', moment().format('DD.MM.YYYY'), {
        shouldValidate: true,
      })
    }
  }, [hasPrevStartValue, open])

  useEffect(() => {
    trigger(['toDate', 'fromDate'])
  }, [watchFields])
  return (
    modalStatus === '' && (
      <>
        <Form layout="vertical">
          <Row gutter={[24, 24]} className={style['row-spacing']}>
            <Col span={7}>
              <Form.Item label={t('businessCards.startDate')}>
                <Controller
                  name="fromDate"
                  control={control}
                  render={({ field, fieldState: { error, invalid } }) => {
                    return (
                      <DatePicker
                        id="fromDate"
                        isError={invalid}
                        placeholder={t('businessCards.notSelected')}
                        disabled={!hasPrevStartValue}
                        errorMessage={error?.message}
                        format="DD.MM.YYYY"
                        allowClear={false}
                        value={
                          field.value ? moment(field.value, 'DD.MM.YYYY') : null
                        }
                        onChange={(date) =>
                          field.onChange(date ? date.format('DD.MM.YYYY') : '')
                        }
                        disabledDate={(current) => {
                          if (!current) return false
                          return !current.isSame(moment(), 'day') // Only allow today
                        }}
                      />
                    )
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={7}>
              <Form.Item label={<>
                {t('businessCards.endDate')}
                          <CustomTooltip
                            placment="topRight"
                            className="table-tooltip"
                            title={t('businessCards.notificationInfo')}
                          >
                            <span style={{cursor:'pointer',display:'flex',alignItems:'center',marginLeft:'4px'}}>
                              <InformationBlueIcon />
                            </span>
                          </CustomTooltip>
                </>}>
                <Controller
                  name="toDate"
                  control={control}
                  render={({ field, fieldState: { error, invalid } }) => {
                    const expDate = getValues('expDate')
                    const maxDate = expDate
                      ? moment(expDate, 'DD.MM.YYYY')
                      : null
                    const minDate = moment().add(1, 'day').startOf('day')

                    return (
                      <DatePicker
                        id="toDate"
                        placeholder={t('businessCards.notSelected')}
                        isError={invalid}
                        errorMessage={error?.message}
                        disabled={checked}
                        format="DD.MM.YYYY"
                        allowClear={false}
                        value={
                          field.value ? moment(field.value, 'DD.MM.YYYY') : null
                        }
                        onChange={(date) =>
                          field.onChange(date ? date.format('DD.MM.YYYY') : '')
                        }
                        disabledDate={(current) => {
                          if (!current) return false
                          if (maxDate && current > maxDate.endOf('day'))
                            return true
                          return current < minDate
                        }}
                      />
                    )
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={8} className={style['checkbox-container']}>
              <Form.Item label={t('businessCards.endCartDate')}>
                <Controller
                  name="toExpDate"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="toExpDate"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked)
                        setEndDate(e.target.checked)
                      }}
                    >
                      {getValues('expDate')}
                    </Checkbox>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 24]} className={style['row-spacing']}>
            <Col span={5}>
              <Controller
                name="amount"
                control={control}
                render={({ field, fieldState: { invalid, error } }) => {
                  return (
                    <InputField
                      label={t('businessCards.limitforBalans')}
                      mask={Number}
                      type="number"
                      onKeyDown={(e) => {
                        const invalidChars = ['-', '+', 'e', 'E', '.']
                        if (invalidChars.includes(e.key)) {
                          e.preventDefault()
                        }
                      }}
                      validateStatus={errors.amount ? 'error' : 'validating'}
                      help={error?.message}
                      {...field}
                      placeholder={t('businessCards.enter')}
                    />
                  )
                }}
              />
            </Col>

            <Col span={3}>
              <Controller
                name="curr"
                control={control}
                render={({ field }) => (
                  <InputField
                    id="curr"
                    label={t('home.currency')}
                    disabled
                    {...field}
                  />
                )}
              />
            </Col>

            <Col span={5}>
              <Controller
                name="count"
                control={control}
                render={({ field }) => (
                  <InputField
                    label={t('businessCards.limitforCount')}
                    validateStatus={errors.count ? 'error' : 'validating'}
                    mask={Number}
                    type="number"
                    onKeyDown={(e) => {
                      const invalidChars = ['-', '+', 'e', 'E', '.']
                      if (invalidChars.includes(e.key)) {
                        e.preventDefault()
                      }
                    }}
                    help={errors.count?.message}
                    {...field}
                  />
                )}
              />
            </Col>

            <Col span={5}>
              <Form.Item
                label={t('businessCards.status')}
                validateStatus={errors.statusForLabel ? 'error' : ''}
                help={errors.statusForLabel?.message}
                style={{ marginTop: '8px' }}
              >
                <Controller
                  name="statusForLabel"
                  control={control}
                  render={({ field }) => {
                    const status = field.value as keyof typeof statusStyles
                    const style = statusStyles[status] || {}
                    return (
                      <Typography.Text
                        id="statusForLabel"
                        {...field}
                        style={{
                          background: style.background || 'transparent',
                          color: style.color || 'inherit',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          marginTop: '4px',
                        }}
                      >
                        {t(`businessCards.${style.text}`) ||
                          t(`businessCards.${field.value}`)}
                      </Typography.Text>
                    )
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className={style['button-container']}>

            <ModalProvider
              title={t('common.attention')}
              centered
              width={500}
              footer={null}
              modalContent={<DisabledLimit />}
            >
              {({ openModal }) =>
                status !== LimitCurrentStatus.InActive &&
                status !== LimitCurrentStatus.Suspended && (
                  <ButtonGlobal
                    disabled={!isValid}
                    customType="danger"
                    onClick={() => openModal('')}
                  >
                    {t('businessCards.deactivate')}
                  </ButtonGlobal>
                )
              }
            </ModalProvider>
            <ModalProvider
              title={t('common.attention')}
              centered
              width={500}
              footer={null}
              modalContent={<AssignLimit />}
            >
              {({ openModal }) =>
                status !== LimitCurrentStatus.Suspended && (
                  <ButtonGlobal
                    disabled={!isValid}
                    customType="primary"
                    onClick={() => openModal('')}
                  >
                    {t('businessCards.assign')}
                  </ButtonGlobal>
                )
              }
            </ModalProvider>
            <ModalProvider
              title={t('common.attention')}
              centered
              width={500}
              footer={null}
              modalContent={<AssignLimit />}
            >
              {({ openModal }) =>
                status === LimitCurrentStatus.Suspended && (
                  <ButtonGlobal
                    disabled={!isValid}
                    customType="primary"
                    onClick={() => openModal('')}
                  >
                    {t('businessCards.activateButton')}
                  </ButtonGlobal>
                )
              }
            </ModalProvider>
          </Row>
        </Form>
      </>
    )
  )
}

export default RegulationLimits
